import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Button } from "@dmca/frontend/components/button";
import { Input } from "@dmca/frontend/components/input";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ConfirmationModal } from "@dmca/frontend/components/modal";
import type { IPlant } from "@dmca/models/interface/plant";
import { fileToBase64 } from "@dmca/frontend/utils/file";

export const Route = createFileRoute("/_authed/_drawerLayout/admin/plant/$id")({
  component: RouteComponent,
  loader: async ({ context: { trpcQcProxy: trpc, qc }, params: { id } }) => {
    const plants = await qc.ensureQueryData(trpc.plant.getAll.queryOptions());
    const plant = plants.find((x) => x.id === id);
    if (!plant) {
      throw notFound();
    }
    return { plants };
  },
});

function RouteComponent() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const id = Route.useParams({
    select: (params) => params.id,
  });
  const navigate = Route.useNavigate();
  const { plants: initialPlants } = Route.useLoaderData();
  const { data: plant } = useQuery(
    trpc.plant.getAll.queryOptions(undefined, {
      select: (data) => data.find((x) => x.id === id)! as IPlant,
      initialData: initialPlants,
    })
  );
  const updateMutation = useMutation(
    trpc.plant.update.mutationOptions({
      onSuccess: (data) => {
        qc.setQueryData(trpc.plant.getAll.queryKey(), (oldData) => {
          if (!oldData) return [data];
          const index = oldData.findIndex((x) => x.id === data.id);
          if (index === -1) return oldData;
          const newData = [...oldData];
          newData[index] = { ...newData[index], name: data.name };
          return newData;
        });

        toast.success("Plant updated successfully.");
      },
      onError: (error) => toast.error(error.message),
    })
  );
  const { data: map } = useQuery(
    trpc.plant.getMap.queryOptions(
      { plantId: id, mapImageKey: plant.mapImageKey },
      { enabled: !!plant.mapImageKey }
    )
  );
  const uploadMutation = useMutation(
    trpc.plant.uploadMap.mutationOptions({
      onSuccess: async (data) => {
        qc.setQueryData(trpc.plant.getAll.queryKey(), (oldData) =>
          oldData?.map((item) =>
            item.id === data.id
              ? { ...item, mapImageKey: data.mapImageKey }
              : item
          )
        );
        await qc.invalidateQueries(trpc.plant.getAll.queryFilter());
        await qc.invalidateQueries(trpc.plant.getMap.queryFilter());
        toast.success("Plant map uploaded successfully");
      },
      onError: async (error) => {
        toast.error(error.message);
        await qc.invalidateQueries(trpc.plant.getAll.queryFilter());
      },
    })
  );
  const removeMapMutation = useMutation(
    trpc.plant.removeMap.mutationOptions({
      onSuccess: async (data) => {
        qc.setQueryData(trpc.plant.getAll.queryKey(), (oldData) =>
          oldData?.map((item) =>
            item.id === data.id
              ? { ...item, mapImageKey: data.mapImageKey }
              : item
          )
        );
        await qc.invalidateQueries(trpc.plant.getAll.queryFilter());
        await qc.invalidateQueries(trpc.cell.getAll.queryFilter());
        toast.success("Plant map removed successfully");
      },
      onError: async (error) => {
        toast.error(error.message);
        await qc.invalidateQueries(trpc.plant.getAll.queryFilter());
      },
    })
  );

  const deleteMutation = useMutation(
    trpc.plant.delete.mutationOptions({
      onSuccess: () => {
        toast.success("Plant deleted successfully.");
        qc.invalidateQueries(trpc.plant.getAll.queryOptions());
        navigate({
          to: "/admin/plant",
          replace: true,
        });
      },
    })
  );

  const onDeletePlantConfirm = () => {
    deleteMutation.mutate({
      ids: [plant.id],
    });
    setShowDeletePlantModal(false);
  };

  const [plantNameValue, setPlantNameValue] = useState("");
  const [showDeletePlantModal, setShowDeletePlantModal] = useState(false);
  useEffect(() => {
    setPlantNameValue(plant?.name ?? "");
  }, [plant?.id, plant?.name]);

  return (
    <>
      <Stack direction="column" spacing={1} component="form" sx={{ mt: 1 }}>
        <Input
          label="Plant Name"
          required
          value={plantNameValue}
          onChange={(e) => setPlantNameValue(e.target.value)}
          error={!plantNameValue.trim()}
          errorText="Plant name is required."
        />
        {plant.mapImageKey && map && (
          <Box
            component="img"
            alt={`${plant.name} map`}
            src={map.url}
            sx={{ width: "100%", maxHeight: 400, objectFit: "contain" }}
          />
        )}
        <Stack direction="row" spacing={1}>
          <Button
            component="label"
            variant="outlined"
            loading={uploadMutation.isPending}
          >
            {plant.mapImageKey ? "Replace Map" : "Upload Map"}
            <input
              hidden
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 4 * 1024 * 1024) {
                  toast.error("Plant maps must be 4 MB or smaller.");
                  return;
                }
                uploadMutation.mutate({
                  plantId: plant.id,
                  expectedMapImageKey: plant.mapImageKey,
                  file: await fileToBase64(file),
                });
              }}
            />
          </Button>
          {plant.mapImageKey && (
            <Button
              color="error"
              variant="outlined"
              loading={removeMapMutation.isPending}
              onClick={() =>
                removeMapMutation.mutate({
                  plantId: plant.id,
                  expectedMapImageKey: plant.mapImageKey,
                })
              }
            >
              Remove Map
            </Button>
          )}
        </Stack>
        <Button
          fullWidth
          disabled={
            (plant && plantNameValue.trim() === plant.name) ||
            !plant ||
            !plantNameValue.trim()
          }
          loading={updateMutation.isPending}
          type="submit"
          onClick={(e) => {
            e.preventDefault();
            if (!plant) return;
            updateMutation.mutate({
              id: plant.id,
              name: plantNameValue,
            });
          }}
        >
          Submit
        </Button>
      </Stack>
      <Divider sx={{ my: 3 }} />
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Advanced
        </AccordionSummary>
        <AccordionDetails>
          <Button
            fullWidth
            color="error"
            onClick={() => setShowDeletePlantModal(true)}
            loading={deleteMutation.isPending}
          >
            Remove Plant
          </Button>
        </AccordionDetails>
      </Accordion>

      {showDeletePlantModal && (
        <ConfirmationModal
          open={showDeletePlantModal}
          onClose={() => setShowDeletePlantModal(false)}
          onConfirm={onDeletePlantConfirm}
        >
          <Typography>
            Are you sure you want to delete this plant? This action is
            irreversible and will remove all associated data, including all
            cells IoT devices associated with this plant.
          </Typography>
          <Typography fontWeight="bold">
            THIS PROCESS IS IRREVERSIBLE!
          </Typography>
        </ConfirmationModal>
      )}
    </>
  );
}
